import React, { useContext, useEffect, useState } from 'react'
import { HeaderProps } from '../../components/Layout/Header'
import axios from 'axios'
import Modal from '../../components/Display/Modal'
import PayrollForm, { ICreatePayroll } from './PayrollForm'
import useSnackBars from '../../hooks/SnackbarConsumerHook'
import Empty from '../../components/Display/Empty'
import PayrollAnimation from '../../assets/animations/payroll.json'
import FloatingMenu from '../../components/Display/FloatingMenu'
import { HiDotsVertical } from 'react-icons/hi'
import { RiTeamFill } from 'react-icons/ri'
import ModalConfirm from '../../components/Display/ModalConfirm'
import { ColumnsTable } from 'safe'
import { ColumnItemWrapper } from '../../components/General/ColumnItemWrapper'
import { motion } from 'framer-motion'
import Table from '../../components/Display/Table'
import AddEmployeePayrollForm from './AddEmployeePayrollForm'
import { FaArrowLeft } from 'react-icons/fa'
import Button from '../../components/General/Button'
import PaymentForm, { IForm } from './PaymentForm'
import { useSelector } from 'react-redux'
import { RootState } from '../../redux/store'
import { ThemeContext } from '../../context/ThemeContext'
import { MdAttachMoney } from 'react-icons/md'

const server = process.env.REACT_APP_SERVER_URL
const Payroll: React.FC<IPayroll> = ({ setHeaderActions }) => {
  const [payroll, setPayroll] = useState<Payroll[]>([])

  const [savingPayroll, setSavingPayroll] = useState<boolean>(false)

  const [onAddModal, setOnAddModal] = useState<boolean>(false)

  const { addAlert } = useSnackBars()
  const { theme } = useContext(ThemeContext)

  const [visibleMenu, setVisibleMenu] = useState<string | null>(null)

  const [payRollToDelete, setPayRollToDelete] = useState<string | null>(null)

  const [selectedPayroll, setSelectedPayroll] = useState<IGetPayroll | null>(
    null
  )

  const [
    selectedPayrollEmployees,
    setSelectedPayrollEmployees,
  ] = useState<IGetPayroll | null>(null)

  const [menuVisible, setMenuVisible] = useState<number | null>(null)

  const [onShowAddEmployee, setOnShowAddEmployee] = useState<boolean>(false)

  const [loadingAddemployee, setLoadingAddemployee] = useState<boolean>(false)

  const [employeeMenuVisible, setEmployeeMenuVisible] = useState<number>(-1)

  const [onShowPayModal, setonShowPayModal] = useState<boolean>(false)

  const [bankOptions, setBankOptions] = useState<
    { value: string; text: string }[]
  >([])

  const [globalBanks, setGlobalBanks] = useState<any[]>([])

  const [onSelectedPayrollToPay, setOnSelectedPayrollToPay] = useState<
    string | null
  >(null)

  const [loadingPayment, setLoadingPayment] = useState<boolean>(false)

  const user = useSelector((state: RootState) => state.auth.user)
  const userRole = user?.['custom:user_role']
  const userPlan = user?.['custom:user_plan']
  const isNonCorporateUser = userPlan === 'pyme' || userPlan === 'emprendedor' || userPlan === 'estudiantil'

  const [
    payRollInfoSelected,
    setPayRollInfoSelected,
  ] = useState<IGetPayroll | null>(null)

  useEffect(() => {
    // No cargar datos si no es plan corporativo
    if (!isNonCorporateUser) {
      onGetPayroll()
    }
  }, [isNonCorporateUser])

  useEffect(() => {
    // No mostrar acciones si no es plan corporativo
    if (isNonCorporateUser) {
      setHeaderActions({})
    } else {
      if (selectedPayrollEmployees) {
        if (userRole !== 'seniat') {
          setHeaderActions({
            addEmployee: () => {
              setOnShowAddEmployee(true)
            },
          })
          return
        }
      }

      if (userRole !== 'seniat') {
        setHeaderActions({
          addAction: () => {
            setOnAddModal(true)
          },
        })
      }
    }

    return () => {
      setHeaderActions(null)
    }
  }, [setHeaderActions, selectedPayrollEmployees, userRole, isNonCorporateUser])

  const onGetPayroll = async (): Promise<void> => {
    let response = await axios.get<{ payrolls: Payroll[] }>(`${server}/payroll`)
    setPayroll(response?.data.payrolls)
  }

  const onSubmit = async (values: ICreatePayroll) => {
    /** Indicador que me ayuda a saber si es modo edicion o no */
    if (selectedPayroll) {
      setSavingPayroll(true)

      try {
        await axios.patch(`${server}/payroll`, {
          id: selectedPayroll?.id,
          name: values?.name,
        })
        addAlert({
          message: 'Nómina editada satisfactoriamente',
          status: 'success',
        })
        await onGetPayroll()
        setOnAddModal(false)
        setSelectedPayroll(null)
      } catch (err) {
        addAlert({
          message: 'Error al editar nómina',
          status: 'error',
        })
      } finally {
        setSavingPayroll(false)
      }

      return
    }

    try {
      setSavingPayroll(true)
      await axios.post(`${server}/payroll`, values)
      addAlert({
        message: 'Nómina creada satisfactoriamente',
        status: 'success',
      })
      await onGetPayroll()
      setOnAddModal(false)
    } catch (err) {
      addAlert({
        message: 'Error al crear nómina',
        status: 'error',
      })
    } finally {
      setSavingPayroll(false)
    }
  }

  const onDeletePayroll = async () => {
    addAlert({
      message: 'Eliminando nómina',
      status: 'loading',
    })
    try {
      await axios.delete(`${server}/payroll?id=${payRollToDelete}`)
      addAlert({
        message: 'Nómina eliminada exitosamente',
        status: 'success',
      })
      await onGetPayroll()
      setPayRollToDelete(null)
    } catch (err) {
      addAlert({
        message: 'Error al eliminar nómina',
        status: 'error',
      })
    }
  }

  const onGetEmployees = async (payroll: Payroll): Promise<void> => {
    const resp = await axios.get<{ payroll: IGetPayroll }>(
      `${server}/payroll?id=${payroll?.id}`
    )
    setSelectedPayrollEmployees(resp?.data?.payroll)
  }

  const onSubmitPaymentForm = async (values: IForm): Promise<void> => {
    let bankCurrency = globalBanks?.find(
      (bank) => bank?.id === values?.accountId
    )

    let outflowBody = {
      beneficiary: {
        other: 'N/A',
      },
      amount: values?.amount,
      date: values?.date,
      description: values?.observation,
      reference: 'N/A',
      type: 'PAYROLL',
      account: {
        id: values?.accountId,
        currency: {
          id: bankCurrency?.idCurrency,
        },
      },
    }

    try {
      setLoadingPayment(true)
      await axios.post(`${server}/payroll/pay`, {
        ...values,
        employees: values.employees,
        payrollId: onSelectedPayrollToPay,
      })
      await axios.post(`${server}/outflow`, outflowBody)

      addAlert({
        message: 'Pago realizado satisfactoriamente',
        status: 'success',
      })
      setonShowPayModal(false)
    } catch (error: any) {
      if (error?.response?.data?.message === 'Payroll already paid') {
        addAlert({
          message: 'Nómina ya pagada',
          status: 'error',
        })
        return
      }

      addAlert({
        message: 'Error al realizar pago',
        status: 'error',
      })
    } finally {
      setLoadingPayment(false)
    }
  }

  const columnsEmployees: ColumnsTable[] = [
    {
      title: 'Nombre',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Documento',
      dataIndex: 'document',
      key: 'document',
    },
    {
      title: 'Estatus',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (id: number, record: any) => (
        <p style={{ color: record?.isActive ? '#209F35' : '#006AEA' }}>
          {record?.isActive ? 'Activo' : 'Permiso'}
        </p>
      ),
    },
    {
      title: '',
      dataIndex: 'id',
      align: 'right',
      width: 50,
      key: 'actions',
      render: (id: number, record: any) => (
        <ColumnItemWrapper floatingMenu>
          {/* <div className="text-gray-500 relative"> */}
          <FloatingMenu
            visible={employeeMenuVisible === id}
            onClose={() => {
              setEmployeeMenuVisible(-1)
            }}
            options={{
              onDelete: async () => {
                try {
                  await axios.delete(
                    `${server}/payroll/employee?employeeId=${id}`
                  )
                  await onGetEmployees(selectedPayrollEmployees as Payroll)
                  addAlert({
                    message: 'Empleado eliminado satisfactoriamente',
                    status: 'success',
                  })
                } catch (err) {
                  addAlert({
                    message: 'Error al eliminar empleado',
                    status: 'error',
                  })
                }
              },
            }}
          >
            <HiDotsVertical
              className="cursor-pointer dark:text-white text-primaryGray-400"
              onClick={() => {
                // setMenuVisible(id)
                setEmployeeMenuVisible(id)
              }}
            />
          </FloatingMenu>
        </ColumnItemWrapper>
      ),
    },
  ]

  // Mostrar mensaje de upgrade si no es plan corporativo
  if (isNonCorporateUser) {
    return (
      <div className="flex flex-col justify-center items-center h-full p-4">
        <div
          className="p-8 rounded-lg shadow-lg max-w-2xl text-center"
          style={{
            backgroundColor: theme === 'dark' ? '#1D1D1D' : '#FFFFFF',
            borderColor: theme === 'dark' ? '#2D2D2D' : '',
            borderWidth: theme === 'dark' ? 1 : 0,
          }}
        >
          <div className="mb-6">
            <MdAttachMoney className="mx-auto mb-4" size={64} style={{ color: '#507CDC' }} />
          </div>
          <h2 className="text-2xl font-bold mb-4 dark:text-white">
            Funcionalidad Premium
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-400 mb-6">
            Para gestionar nóminas debes mejorar tu plan a <strong className="text-primary">"Corporativo"</strong>
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-500">
            El plan Corporativo incluye gestión completa de nóminas con cálculo automático de salarios, prestaciones sociales, deducciones, reportes fiscales, pagos bancarios y mucho más.
          </p>
        </div>
      </div>
    )
  }

  return (
    <>
      {selectedPayrollEmployees !== null && (
        <>
          <div className="flex-row flex justify-between mb-4">
            <div className="flex space-x-2 mt-3 px-1 block">
              <Button
                classes="border-none"
                onClick={() => {
                  setSelectedPayrollEmployees(null)
                }}
                type="arrow"
                warehouseType="SALE FLOOR"
              >
                <FaArrowLeft size={30} />
              </Button>

              <h1 className="dark:text-primaryGreen-300 text-primaryGreen-500">
                {selectedPayrollEmployees?.name}
              </h1>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <Table
              data={selectedPayrollEmployees.employees}
              columns={columnsEmployees}
              // pages={numberOfPages}
              // pageSelected={currentPage}
              // loading={loading}
              showPagination={false}
              setPageSelected={(page) => {}}
              // setLoading={setLoading}
            />
          </motion.div>
        </>
      )}

      {payroll.length === 0 && selectedPayrollEmployees === null && (
        <>
          <Empty
            animationProps={{
              animation: PayrollAnimation,
              width: 150,
              height: 150,
            }}
          >
            <p className="font-semibold max-w-xs text-center my-4 text-sm text-coolGray-500">
              Aqui puedes crear, pagar, eliminar y editar las nóminas.
            </p>
          </Empty>
        </>
      )}

      {payroll.length > 0 && selectedPayrollEmployees === null && (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-6 px-1">
          {payroll.map((v) => (
            <div className="2xl:w-96 w-80 shadow-lg rounded-lg p-4 text-xl flex justify-between relative dark:bg-black m-auto">
              <div className="w-full cursor-pointer" style={{ opacity: '1' }}>
                <div className="flex mb-3 justify-between flex-row">
                  <div
                    className="flex flex-row"
                    onClick={() => {
                      onGetEmployees(v)
                    }}
                    style={{ flexGrow: 1 }}
                  >
                    <div className="mr-4 text-primary-600">
                      <RiTeamFill size="1.2rem" className="text-primary" />
                    </div>
                    <div
                      className="truncate text-primary-600 font-medium text-sm dark:text-white grow"
                      style={{ flexGrow: 1 }}
                    >
                      {v.name}
                    </div>
                  </div>

                  <FloatingMenu
                    onClose={() => setVisibleMenu(null)}
                    visible={visibleMenu === v.id}
                    options={{
                      onEdit: async () => {
                        let response = await axios.get<{
                          payroll: IGetPayroll
                        }>(`${server}/payroll?id=${v?.id}`)
                        setSelectedPayroll(response?.data?.payroll)
                        setOnAddModal(true)
                      },
                      onDelete: () => {
                        setPayRollToDelete(v?.id)
                      },
                      onPay: async () => {
                        const [banksResp, payRollInfoResp] = await Promise.all([
                          axios.get(`${server}/finances/account`),
                          axios.get<{ payroll: IGetPayroll }>(
                            `${server}/payroll?id=${v?.id}`
                          ),
                        ])
                        setGlobalBanks(banksResp.data?.accounts)
                        // const resp = await
                        const banks = banksResp.data?.accounts.map((v) => ({
                          value: v?.id,
                          text: v?.description,
                        }))
                        setPayRollInfoSelected(payRollInfoResp.data.payroll)
                        setBankOptions(banks)
                        setOnSelectedPayrollToPay(v?.id)
                        setonShowPayModal(true)
                      },
                    }}
                  >
                    {userRole !== 'seniat' && (
                      <HiDotsVertical
                      className="cursor-pointer text-gray-400"
                        onClick={() => setVisibleMenu(v.id)}
                      />
                    )}
                  </FloatingMenu>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal
        display={onAddModal}
        onClose={() => {
          setOnAddModal(false)
          if (selectedPayroll) {
            setSelectedPayroll(null)
          }
        }}
        normalClasses="overflow-visible"
        xlClasses="w-[650px]"
      >
        <PayrollForm
          onSubmit={onSubmit}
          iSavingPayroll={savingPayroll}
          onCancel={() => {
            setOnAddModal(false)
            if (selectedPayroll) {
              setSelectedPayroll(null)
            }
          }}
          selectedPayroll={
            selectedPayroll
              ? {
                  name: selectedPayroll.name,
                  salaryFreq: selectedPayroll.salaryFreq,
                }
              : null
          }
        />
      </Modal>

      <ModalConfirm
        title={'¿Está seguro que desea eliminar la nómina?'}
        displayConfirmModal={payRollToDelete !== null}
        onCancel={() => {
          setPayRollToDelete(null)
        }}
        onConfirm={() => {
          onDeletePayroll()
        }}
      />

      <Modal
        display={onShowAddEmployee}
        onClose={() => {
          setOnShowAddEmployee(false)
        }}
      >
        <AddEmployeePayrollForm
          loadingAddemployee={loadingAddemployee}
          onCancel={() => {
            setOnShowAddEmployee(false)
          }}
          onSubmit={async (selectedEmployee) => {
            setLoadingAddemployee(true)
            try {
              let resp = await axios.post(`${server}/payroll/employee`, {
                employeeId: selectedEmployee.id,
                payrollId: selectedPayrollEmployees?.id,
              })
              addAlert({
                message: 'Empleado agregado satisfactoriamente',
                status: 'success',
              })
              onGetEmployees(selectedPayrollEmployees as Payroll)
              setOnShowAddEmployee(false)
            } catch (err: any) {
              if (
                err?.response?.data?.message ===
                'Employee already added to payroll'
              ) {
                addAlert({
                  message: 'Empleado ya agregado',
                  status: 'error',
                })
                return
              }

              addAlert({
                message: 'Error al agregar empleado',
                status: 'error',
              })
            } finally {
              setLoadingAddemployee(false)
            }
          }}
        />
      </Modal>

      <Modal
        display={onShowPayModal}
        onClose={() => {
          setonShowPayModal(false)
        }}
        xlClasses="xl:w-7/12"
      >
        <PaymentForm
          bankOptions={bankOptions}
          payrollInfoSelected={payRollInfoSelected!}
          onCancel={() => {
            setonShowPayModal(false)
          }}
          onSubmit={onSubmitPaymentForm}
          isLoading={loadingPayment}
        />
      </Modal>
    </>
  )
}

interface IPayroll {
  setHeaderActions: React.Dispatch<
    React.SetStateAction<HeaderProps['headerActions'] | null | undefined>
  >
}

interface Payroll {
  id: string
  name: string
}

export interface Employee {
  id: number
  name: string
  document: string
  isActive: boolean
  baseSalary: string
  benefits: { amount: string; description: string }[]
  deductions: { amount: string; description: string }[]
  currencyId: string
}

export interface IGetPayroll {
  id: string
  name: string
  salaryFreq: 'WEEKLY' | 'BIWEEKLY' | 'MONTHLY'
  employees: Employee[]
  paidPayrolls: {
    date: Date
    amount: number
    observation: string
  }[]
}

export default Payroll
